import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle, Info, XCircle } from 'lucide-react';

const Notification = ({ message, type = 'info', onClose, duration = 5000 }) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const icons = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertCircle,
    info: Info
  };

  const colors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  };

  const iconColors = {
    success: 'text-green-600',
    error: 'text-red-600',
    warning: 'text-yellow-600',
    info: 'text-blue-600'
  };

  const Icon = icons[type] || Info;

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, x: '-50%' }}
      animate={{ opacity: 1, y: 0, x: '-50%' }}
      exit={{ opacity: 0, y: -20, x: '-50%' }}
      className={`fixed top-4 left-1/2 z-50 min-w-[300px] max-w-[500px] rounded-lg border shadow-lg ${colors[type]}`}
    >
      <div className="flex items-start gap-3 p-4">
        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColors[type]}`} />
        <div className="flex-1">
          {typeof message === 'string' && message.includes('\n') ? (
            <ul className="text-sm font-medium space-y-1 list-none">
              {message.split('\n').filter(Boolean).map((line, i) => (
                <li key={i} className="flex items-start gap-1">
                  {i > 0 && <span className="mt-0.5 text-current opacity-50">›</span>}
                  <span className={i === 0 ? 'font-semibold' : ''}>{line}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm font-medium">{message}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className={`flex-shrink-0 ${iconColors[type]} hover:opacity-70 transition-opacity`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export const NotificationContainer = ({ notifications, onRemove }) => {
  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 space-y-2 pointer-events-none">
      <AnimatePresence>
        {notifications.map((notification) => (
          <div key={notification.id} className="pointer-events-auto">
            <Notification
              message={notification.message}
              type={notification.type}
              onClose={() => onRemove(notification.id)}
              duration={notification.duration}
            />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default Notification;

